import React, { useState } from "react";

import {
  Box,
  TextField,
  Typography,
  IconButton,
  Button,
  Collapse,
  Divider,
  Select,
  MenuItem,
  Stack,
  ToggleButton,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import TitleIcon from "@mui/icons-material/Title";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatColorResetIcon from "@mui/icons-material/FormatColorReset";

import { TextOverlay, TextAnimation, BackgroundStyle } from "../../types";

const ANIMATION_OPTIONS: { value: TextAnimation; label: string }[] = [
  { value: "none", label: "None" },
  { value: "fade-in", label: "Fade In" },
  { value: "fade-out", label: "Fade Out" },
  { value: "slide-up", label: "Slide Up" },
  { value: "slide-down", label: "Slide Down" },
  { value: "slide-left", label: "Slide Left" },
  { value: "slide-right", label: "Slide Right" },
  { value: "zoom-in", label: "Zoom In" },
  { value: "zoom-out", label: "Zoom Out" },
  { value: "pop", label: "Pop" },
  { value: "typewriter", label: "Typewriter" },
  { value: "bounce", label: "Bounce" },
  { value: "pulse", label: "Pulse" },
  { value: "rotate", label: "Rotate" },
];

const FONT_OPTIONS = [
  "Inter",
  "Montserrat",
  "Outfit",
  "Bebas Neue",
  "Oswald",
  "Josefin Sans",
  "Playfair Display",
  "Arvo",
];

const toggleSx = {
  width: 34,
  height: 34,
  borderRadius: "8px !important",
  border: 1,
  borderColor: "divider",
};

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        mb: 0.75,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        fontSize: 11,
      }}
    >
      {children}
    </Typography>
  );
}

interface ColorSwatchProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
}

function ColorSwatch({ label, value, onChange, disabled }: ColorSwatchProps) {
  return (
    <Tooltip title={`${label} color`}>
      <Box
        component="label"
        sx={{
          position: "relative",
          width: 34,
          height: 34,
          borderRadius: "50%",
          border: 2,
          borderColor: "divider",
          bgcolor: value,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.35 : 1,
          boxShadow: "inset 0 0 0 2px rgba(0,0,0,0.25)",
          transition: "transform 120ms",
          "&:hover": { transform: disabled ? "none" : "scale(1.08)" },
        }}
      >
        <input
          type="color"
          value={value}
          disabled={disabled}
          aria-label={`${label} color`}
          onChange={(e) => onChange(e.target.value)}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            width: "100%",
            height: "100%",
            cursor: "inherit",
          }}
        />
      </Box>
    </Tooltip>
  );
}

interface TextOverlayPanelProps {
  textOverlays: TextOverlay[];
  videoDurationMs: number;
  currentTimeMs: number;
  onAddText: (overlay: TextOverlay) => void;
  onRemoveText: (id: string) => void;
  onUpdateText: (id: string, updates: Partial<TextOverlay>) => void;
}

const formatTime = (ms: number) => {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const parseTime = (timeStr: string): number => {
  const parts = timeStr.split(":");
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10) || 0;
    const s = parseInt(parts[1], 10) || 0;
    return (m * 60 + s) * 1000;
  }
  return 0;
};

export default function TextOverlayPanel({
  textOverlays,
  videoDurationMs,
  currentTimeMs,
  onAddText,
  onRemoveText,
  onUpdateText,
}: TextOverlayPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(
    textOverlays.length === 1 ? (textOverlays[0]?.id ?? null) : null,
  );

  const handleAddText = () => {
    const overlay: TextOverlay = {
      id: crypto.randomUUID(),
      text: "Your text here",
      x: 30,
      y: 40,
      width: 40,
      height: 15,
      startMs: currentTimeMs,
      endMs: videoDurationMs,
      fontSize: 24,
      fontFamily: "Inter",
      color: "#ffffff",
      backgroundColor: "rgba(0,0,0,0.5)",
      backgroundStyle: BackgroundStyle.SOLID,
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
      animation: "none",
    };
    onAddText(overlay);
  };

  const isTransparentBg = (overlay: TextOverlay) =>
    overlay.backgroundColor === "transparent";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Button
        variant="contained"
        disableElevation
        startIcon={<AddIcon />}
        onClick={handleAddText}
        fullWidth
        sx={{ textTransform: "none", fontWeight: 600, py: 1 }}
      >
        Add text
      </Button>

      {textOverlays.length === 0 && (
        <Box
          sx={{
            py: 4,
            px: 2,
            textAlign: "center",
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <TitleIcon sx={{ fontSize: 32, color: "text.disabled", mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Add a headline, a hook or a call to action. It's placed at the
            playhead and you can drag it on the video.
          </Typography>
        </Box>
      )}

      {textOverlays.map((overlay) => {
        const isExpanded = expandedId === overlay.id;
        return (
          <Box
            key={overlay.id}
            sx={{
              border: 1,
              borderColor: isExpanded ? "primary.main" : "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "background.paper",
              transition: "border-color 120ms",
            }}
          >
            <Box
              role="button"
              tabIndex={0}
              onClick={() =>
                setExpandedId((prev) =>
                  prev === overlay.id ? null : overlay.id,
                )
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target === e.currentTarget) {
                  setExpandedId((prev) =>
                    prev === overlay.id ? null : overlay.id,
                  );
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: 1.25,
                cursor: "pointer",
                "&:hover": { bgcolor: "action.hover" },
                "&:hover .overlay-delete": { opacity: 1 },
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontFamily: `"${overlay.fontFamily}", sans-serif`,
                  fontWeight: overlay.bold ? 800 : 500,
                  fontStyle: overlay.italic ? "italic" : "normal",
                  fontSize: 16,
                  color: overlay.color,
                  bgcolor: isTransparentBg(overlay)
                    ? "grey.800"
                    : overlay.backgroundColor,
                  border: 1,
                  borderColor: "divider",
                }}
              >
                Aa
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {overlay.text || "Text overlay"}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {formatTime(overlay.startMs)} – {formatTime(overlay.endMs)}
                  {overlay.animation && overlay.animation !== "none"
                    ? ` · ${
                        ANIMATION_OPTIONS.find(
                          (o) => o.value === overlay.animation,
                        )?.label
                      }`
                    : ""}
                </Typography>
              </Box>
              <Tooltip title="Delete">
                <IconButton
                  className="overlay-delete"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveText(overlay.id);
                  }}
                  sx={{
                    opacity: isExpanded ? 1 : 0,
                    transition: "opacity 120ms",
                    "&:hover": { color: "error.main" },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  color: "text.secondary",
                  transform: isExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 150ms",
                }}
              />
            </Box>

            <Collapse in={isExpanded} unmountOnExit>
              <Stack
                spacing={2}
                sx={{
                  px: 1.5,
                  pb: 2,
                  pt: 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Type your text"
                  multiline
                  minRows={2}
                  maxRows={5}
                  value={overlay.text}
                  onChange={(e) =>
                    onUpdateText(overlay.id, { text: e.target.value })
                  }
                  fullWidth
                />

                <Box>
                  <SectionLabel>Font</SectionLabel>
                  <Stack direction="row" spacing={1}>
                    <Select
                      size="small"
                      value={overlay.fontFamily}
                      onChange={(e) =>
                        onUpdateText(overlay.id, {
                          fontFamily: e.target.value as string,
                        })
                      }
                      sx={{
                        flex: 1,
                        fontSize: 14,
                        fontFamily: `"${overlay.fontFamily}", sans-serif`,
                      }}
                    >
                      {FONT_OPTIONS.map((font) => (
                        <MenuItem
                          key={font}
                          value={font}
                          sx={{ fontFamily: font }}
                        >
                          {font}
                        </MenuItem>
                      ))}
                    </Select>
                    <TextField
                      size="small"
                      type="number"
                      value={overlay.fontSize}
                      onChange={(e) =>
                        onUpdateText(overlay.id, {
                          fontSize: Math.max(
                            8,
                            parseInt(e.target.value, 10) || 24,
                          ),
                        })
                      }
                      sx={{ width: 84 }}
                      InputProps={{
                        inputProps: { min: 8, max: 120, "aria-label": "Size" },
                        endAdornment: (
                          <Typography variant="caption" color="text.secondary">
                            px
                          </Typography>
                        ),
                      }}
                    />
                  </Stack>
                </Box>

                <Box>
                  <SectionLabel>Style</SectionLabel>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flexWrap: "wrap", rowGap: 1 }}
                  >
                    <ToggleButton
                      value="bold"
                      size="small"
                      selected={overlay.bold}
                      onChange={() =>
                        onUpdateText(overlay.id, { bold: !overlay.bold })
                      }
                      sx={toggleSx}
                      aria-label="Bold"
                    >
                      <FormatBoldIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton
                      value="italic"
                      size="small"
                      selected={overlay.italic}
                      onChange={() =>
                        onUpdateText(overlay.id, { italic: !overlay.italic })
                      }
                      sx={toggleSx}
                      aria-label="Italic"
                    >
                      <FormatItalicIcon fontSize="small" />
                    </ToggleButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    <ColorSwatch
                      label="Text"
                      value={overlay.color}
                      onChange={(color) => onUpdateText(overlay.id, { color })}
                    />
                    <ColorSwatch
                      label="Background"
                      value={
                        isTransparentBg(overlay) ||
                        overlay.backgroundColor.startsWith("rgba")
                          ? "#000000"
                          : overlay.backgroundColor
                      }
                      disabled={isTransparentBg(overlay)}
                      onChange={(color) =>
                        onUpdateText(overlay.id, {
                          backgroundColor: `${color}80`,
                        })
                      }
                    />
                    <Tooltip
                      title={
                        isTransparentBg(overlay)
                          ? "Add background"
                          : "Remove background"
                      }
                    >
                      <ToggleButton
                        value="no-bg"
                        size="small"
                        selected={isTransparentBg(overlay)}
                        onChange={() =>
                          onUpdateText(overlay.id, {
                            backgroundColor: isTransparentBg(overlay)
                              ? "rgba(0,0,0,0.5)"
                              : "transparent",
                          })
                        }
                        sx={toggleSx}
                        aria-label="No background"
                      >
                        <FormatColorResetIcon fontSize="small" />
                      </ToggleButton>
                    </Tooltip>
                  </Stack>
                </Box>

                <Box>
                  <SectionLabel>Animation</SectionLabel>
                  <Select
                    size="small"
                    fullWidth
                    value={overlay.animation || "none"}
                    onChange={(e) =>
                      onUpdateText(overlay.id, {
                        animation: e.target.value as TextAnimation,
                      })
                    }
                    sx={{ fontSize: 14 }}
                  >
                    {ANIMATION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <SectionLabel>Timing</SectionLabel>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      size="small"
                      defaultValue={formatTime(overlay.startMs)}
                      onBlur={(e) =>
                        onUpdateText(overlay.id, {
                          startMs: parseTime(e.target.value),
                        })
                      }
                      InputProps={{
                        inputProps: { "aria-label": "Start" },
                        startAdornment: (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            From
                          </Typography>
                        ),
                      }}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      defaultValue={formatTime(overlay.endMs)}
                      onBlur={(e) =>
                        onUpdateText(overlay.id, {
                          endMs: parseTime(e.target.value),
                        })
                      }
                      InputProps={{
                        inputProps: { "aria-label": "End" },
                        startAdornment: (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            To
                          </Typography>
                        ),
                      }}
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.75 }}
                  >
                    Format m:ss. You can also drag the clip on the timeline.
                  </Typography>
                </Box>
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}
